import { useCallback } from "react";
import { UserActionTypes, useUserDispatch, useUserState } from "src/contexts/UserContext";
import { CompanyListModel } from "src/modules/company/types/Company";
import { UserRoles } from "src/modules/user/types/UserRoles";
import { Storage } from "src/utils/storage";

export const getAccessToken = () => {
    return Storage.getItem("userState") ? Storage.getItem("userState").accessToken : null;
};

export const updateAccessToken = (token: string) => {
    const userState = Storage.getItem("userState");
    Storage.setItem("userState", { ...userState, accessToken: token });
};

export const getRefreshToken = () => {
    return Storage.getItem("userState") ? Storage.getItem("userState").refreshToken : null;
};

const useAuth = () => {
    const userState = useUserState() as any;
    const dispatch = useUserDispatch();

    const isAuthenticated = useCallback(() => userState?.isAuthenticated, [userState]);

    const hasFullAccess = () => {
        const permissions = Storage.getItem("userRoles") || null;
        if (permissions) {
            return !!permissions?.hasFullAccess;
        }

        return false;
    };

    const hasPermission = (perm: UserRoles) => {
        const permissions = Storage.getItem("userRoles") || null;
        if (permissions === null) {
            return null;
        }
        if (permissions) {
            return !!(
                permissions?.hasFullAccess ||
                (permissions && permissions?.roles.findIndex((r: UserRoles) => r === perm) > -1)
            );
        }

        return false;
    };

    const contextLogin = (email: string, accessToken: string, refreshToken: string) => {
        dispatch({
            type: UserActionTypes.SET,
            payload: { email, isAuthenticated: true },
        });
        Storage.setItem("userState", { email, accessToken, refreshToken, isAuthenticated: true });
    };

    const contextCompanySet = (company: CompanyListModel) => {
        dispatch({
            type: UserActionTypes.SET_COMPANY,
            payload: { ...company },
        });
    };

    const contextRolesSet = (roles: string[], fullAccess: boolean) => {
        dispatch({
            type: UserActionTypes.SET_ROLES,
            payload: { roles: { ...roles }, fullAccess },
        });
    };

    const contextLogout = (onSuccess?: () => void) => {
        dispatch({
            type: UserActionTypes.CLEAR,
        });
        dispatch({
            type: UserActionTypes.SET_COMPANY,
            payload: null,
        });
        Storage.removeItem("userState");
        Storage.removeItem("userRoles");
        Storage.removeItem("currentCompany");
        Storage.removeItem("messagesWss");

        if (onSuccess) {
            onSuccess();
        }
    };

    return {
        userState,
        contextLogin,
        contextCompanySet,
        contextLogout,
        getAccessToken,
        contextRolesSet,
        isAuthenticated,
        hasPermission,
        hasFullAccess,
    };
};

export default useAuth;
